import React, { useMemo } from 'react';
import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../../../components/Spinner';
import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../../../../../constants/duskIds';
import { useAllCompanyPolicyTypesData } from '../../../../../../../../services/company-service';
import {
  useActiveRecordPolicies, useArchivedRecordPolicies,
  useRecordItemRequirements,
  useRecordLanguageRequirements,
  useRecordPolicyRequirements
} from '../../../../../../../../services/record-service';
import InsuredSectionDropdown from '../InsuredSectionDropdown';

import PolicyTable from './components/PolicyTable';
import PolicyTableIconTray from './components/PolicyTableIconTray';
import { v3MatchPoliciesToRequirements } from './util';

const PolicyRequirements = ({ companyId, insuredId, recordId, jobId }) => {
  // preload language requirements
  useRecordLanguageRequirements(recordId);
  const { data: allPolicyTypes, isLoading: allPolicyTypesLoading, isFetching: policyTypesFetching } = useAllCompanyPolicyTypesData(companyId);
  const { data: policyRequirements, isLoading: policyRequirementsLoading, isFetching: policyRequirementsFetching } = useRecordPolicyRequirements(recordId);
  const { data: itemRequirements, isLoading: itemRequirementsLoading } = useRecordItemRequirements(recordId);
  const { data: policies, isLoading: policiesLoading, isFetching: policiesFetching } = useActiveRecordPolicies(recordId);

  // prefetch archived policies
  useArchivedRecordPolicies(recordId);

  const matchedPoliciesAndRequirements = useMemo(() => {
    if (policyTypesFetching || allPolicyTypesLoading || policyRequirementsLoading || itemRequirementsLoading || policiesLoading) {
      return null;
    }

    const itemReqs = itemRequirements ?? [];
    const policyReqs = policyRequirements ?? [];

    return v3MatchPoliciesToRequirements(allPolicyTypes, policies, [
      ...itemReqs,
      ...policyReqs
    ]);
  }, [allPolicyTypes, policyRequirements, itemRequirements, policies]);

  if (!recordId) {
    return null;
  }

  if (!matchedPoliciesAndRequirements) {
    return (
      <Stack mb={8}>
        <Skeleton height="18px" />
        <Skeleton height="18px" />
        <Skeleton height="18px" />
      </Stack>
    );
  }

  return (
    <InsuredSectionDropdown
      data-cy={CypressIds.requiredPoliciesSection}
      icons={(jobId && recordId)
        ? (
          <PolicyTableIconTray
            companyId={companyId}
            insuredId={insuredId}
            jobId={jobId}
            recordId={recordId}
          />
        )
        : <LoadingSpinner spinnerSize="sm" />}
      isFetching={policyRequirementsFetching || policiesFetching}
      openByDefault={matchedPoliciesAndRequirements.length > 0}
      title="Required Policies"
    >
      {!matchedPoliciesAndRequirements
        ? (
          <Flex
            justifyContent="center"
            minH="200px"
            position="relative"
          >
            <LoadingSpinner isInline />
          </Flex>
        )
        : (
          <PolicyTable
            companyId={companyId}
            insuredId={insuredId}
            jobId={jobId}
            matchedPoliciesAndRequirements={matchedPoliciesAndRequirements}
            recordId={recordId}
          />
        )}

    </InsuredSectionDropdown>
  );
};

PolicyRequirements.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default PolicyRequirements;
